import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Alert } from '@openx/design-system/components/Alert/Alert.tsx';
import { Button } from '@openx/design-system/components/Button/Button.tsx';
import { Paper } from '@openx/design-system/components/Paper/Paper.tsx';
import { Skeleton } from '@openx/design-system/components/Skeleton/Skeleton.tsx';
import type {} from '@openx/design-system/theme/tokens/typography.ts';
import type { ReactNode } from 'react';

export type DealInsightsV2SectionState = 'loading' | 'error' | 'empty' | 'content';

export type DealInsightsV2SectionProps = {
  title: string;
  state: DealInsightsV2SectionState;
  /** First line of the empty state; the "Adjust..." hint is added by the section. */
  emptyMessage: string;
  onRetry?: () => void;
  children?: ReactNode;
  'data-test'?: string;
};

// DS `Card` is not used on purpose: its title is a `div` (not a heading) and its `action` is a detached footer
// region, while sections need a heading. Must render inside the DS theme.
export const DealInsightsV2Section = ({
  title,
  state,
  emptyMessage,
  onRetry,
  children,
  'data-test': dataTest = 'deal-insights-v2-section',
}: DealInsightsV2SectionProps) => {
  return (
    // Flat white surface on the grey page background: no border, no shadow (DS Paper `elevation={0}`).
    // DS Paper is a bare MUI re-export, so padding/radius are set here: p 3 = 24px, borderRadius 2 = 8px (theme spacing 8, shape 4).
    <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }} data-test={dataTest}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h3" component="h2">
          {title}
        </Typography>
      </Box>
      {state === 'loading' && <Skeleton variant="rounded" height={112} data-test={`${dataTest}-skeleton`} />}
      {state === 'error' && (
        <Alert
          severity="error"
          title="Unable to load this section."
          data-test={`${dataTest}-error`}
          action={
            <Button variant="text" size="medium" onClick={onRetry}>
              Retry
            </Button>
          }
        />
      )}
      {state === 'empty' && (
        <Box
          data-test={`${dataTest}-empty`}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}
        >
          <Typography variant="body1Bold">{emptyMessage}</Typography>
          <Typography variant="body1" color="textSecondary">
            Adjust the date range or filters to try again.
          </Typography>
        </Box>
      )}
      {state === 'content' && children}
    </Paper>
  );
};
